/* eslint-disable camelcase */
import axios from 'axios';
import { useFormik } from 'formik';
import { camelCase, get, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { ButtonGroup, Col, Form, Image, Row, Spinner } from 'react-bootstrap';
import DraggableList from "react-draggable-list";
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import SlidingPane from "react-sliding-pane";
import * as Yup from 'yup';
import Button from '../../../../shared/components/Button';
import Checkbox from '../../../../shared/components/Checkbox/Checkbox';
import Dropzone, { DROPZONE_STATE } from '../../../../shared/components/Dropzone/Dropzone';
import FormControl from '../../../../shared/components/Form/FormControl';
import ObjectAttribute, { generateOASchema, validateObjAttrValuesInput } from '../../../../shared/components/ObjectAttribute/ObjectAttribute';
import { extractValuesFromAttrs } from '../../../../shared/components/ObjectAttribute/extractValues';
import ResponsiveHeader from '../../../../shared/components/ResponsiveHeader';
import Select from '../../../../shared/components/Select';
import { ACTIONS, CONTENT_STATUSES, CONTENT_TYPE } from '../../../../utils/constants';
import { scrollToHashInDiv } from '../../../../utils/scrolling';
import BadgeStatus from './BadgeStatus';
import CarouselListItem from './CarouselListItem';
import './DraftSidePane.scss';

const IMAGES_TYPES = ['.jpeg', '.jpg', '.png', '.gif', '.webp', '.svg', '.bmp', '.tiff', '.ico'];
const VIDEO_TYPES = ['.mov', '.mp4'];

/**
 *
 * @param {{
 * open: boolean
 * draft: any
 * onRequestClose: func
 * onAfterClose: onAfterClose
 * }} params
 * @returns
 */
const DraftSidePane = ({
  open,
  draft,
  status,
  readOnly,
  confirmNewContent,
  onRequestClose,
  onAfterClose,
}) => {
  const offer = useSelector(store => get(store, 'offer.offer'));
  const { t } = useTranslation();

  // Kept outside `state` because the location tags request merges a stale copy of `state` when it resolves
  const [showQuickCheck, setShowQuickCheck] = useState(confirmNewContent && !draft && !readOnly);
  const [newContentConfirmed, setNewContentConfirmed] = useState(false);
  // The past version picked in the versions select, null while viewing the current one
  const [selectedVersion, setSelectedVersion] = useState(null);

  const gwsValidationSchema = () => ({
    googleWebStoryURL:
      Yup.string().url().required(t('influencers.common.forms.is_required'))
  });

  const postValidationSchema = (values) => ({
    mediaUrl: Yup.string().required(t('influencers.common.forms.is_required')),
    caption: values.contentType === CONTENT_TYPE.STORY
      ? Yup.string().trim()
      : Yup.string().trim().required(t('influencers.common.forms.is_required'))
  });

  const userPostDraftTypeFieldsSchema = (contentTypefields) => {
    if (!contentTypefields) return;

    return ({
      story: !contentTypefields.includes('story')
        ? Yup.string().notRequired()
        : Yup.string().required(t('influencers.common.forms.is_required')),
      mediaUrl: !contentTypefields.includes('media')
        ? Yup.mixed().notRequired()
        : Yup.mixed().required(t('influencers.common.forms.is_required')),
      caption: !contentTypefields.includes('caption')
        ? Yup.string().trim()
        : Yup.string().trim().required(t('influencers.common.forms.is_required'))
    });
  };

  const validationSchema = Yup.lazy((values) => {

    let contentTypefields;
    const editableAttrsSchema = generateOASchema(object_attribute_metadata, t);
    let objSchema;

    if (values.contentType === 'googleWebStory') {
      objSchema = gwsValidationSchema();
    } else if (
      offer.user_post_draft_types_enabled &&
      values.contentType !== CONTENT_TYPE.POST
    ) {
      if (state.editing) {
        contentTypefields = selectedType && Object.keys(selectedType.fields);
      } else {
        const selectedType = offer.user_post_draft_types.find(type => camelCase(type.label) === values.contentType);

        contentTypefields = selectedType && Object.keys(selectedType.fields);
      }

      objSchema = userPostDraftTypeFieldsSchema(contentTypefields);
    } else {
      objSchema = postValidationSchema(values);
    }
    return Yup.object({
      ...objSchema,
      ...editableAttrsSchema
    });
  });

  const object_attribute_metadata = useMemo(() => offer.object_attribute_metadata
    .filter(ob => ob.object_type_form_string === "user_post_draft")
    .filter(ob => ob.is_visible_to_influencer), [offer.object_attribute_metadata]);

  const userPostDraftTypeObjAttrs = useMemo(() => offer.user_post_draft_types.map(type => type.object_attribute_metadata.filter(ob => ob.is_visible_to_influencer)
    .filter(ob => ob.object_type_form_string === "user_post_draft")).flat(), [offer.user_post_draft_types]);

  const [state, setState] = useState({
    loading: false,
    success: undefined,
    editing: !readOnly && !!draft,
    showDeletePrompt: false,
    action: null,
    file: null,
    locationTags: [],
    locationTagsDownloaded: false,
    isCurrentVersion: true,
    error: null
  });

  useEffect(() => {
    if (state.locationTagsDownloaded) return;

    axios.get('/api/v1/tags', {
      params: { tag_type: 'contract:location' }
    }).then(({ data }) => {
      setState({
        ...state,
        locationTags: data,
        locationTagsDownloaded: true
      });
    }).catch(function (error) {
      console.log(error);
    });
  }, [state.file, state.locationTags]);

  const getContentType = (content) => {
    if (!content) return null;
    if (content.is_google_web_story) return CONTENT_TYPE.GOOGLE_WEB_STORY;
    if (content.story) return CONTENT_TYPE.STORY;
    if (content.short) return CONTENT_TYPE.YOUTUBE_SHORT;

    const selectedType = content.user_post_draft_type;
    if (selectedType) return camelCase(selectedType.label);

    return CONTENT_TYPE.POST;
  };

  const extractValues = (content) => ({
    title: get(content, 'title', ''),
    contentType: getContentType(content) || CONTENT_TYPE.POST,
    caption: get(content, 'caption', ''),
    mediaUrl: get(content, 'media_url', ''),
    carousel_media_urls: get(content, 'carousel_media_urls', []).map((fileUrl) => ({ fileUrl })),
    googleWebStoryURL: get(content, 'google_web_story_url', ''),
    locationTags: get(content, 'location_tags') || [],
    generalTags: get(content, 'general_tags', ''),
    ...(extractValuesFromAttrs(object_attribute_metadata, get(content, 'object_attribute_values', {}))),
    ...(extractValuesFromAttrs(userPostDraftTypeObjAttrs, get(content, 'object_attribute_values', {})))
  });

  const formik = useFormik({
    initialValues: { ...extractValues(draft), },
    validationSchema,
    onSubmit: values => {
      if (readOnly) return;

      setState({ ...state, loading: true });
      let promise = null;

      const oas = {};
      object_attribute_metadata.concat(userPostDraftTypeObjAttrs).forEach(objectAttribute => {
        oas[objectAttribute.code] = values[objectAttribute.code];
      });

      const data = {
        user_post_draft: values.contentType === 'googleWebStory' ? {
          google_web_story_url: values.googleWebStoryURL,
          user_post_draft_type_id: (selectedType && selectedType.id),
          ...(oas),
        } : {
          caption: values.caption,
          media_url: values.mediaUrl,
          carousel_media_urls: values.carousel_media_urls.map((file) => file.fileUrl),
          story: values.contentType === CONTENT_TYPE.STORY,
          title: values.title,
          general_tags: values.generalTags,
          location_tags: values.locationTags,
          user_post_draft_type_id: (selectedType && selectedType.id),
          post_type: (values.contentType === 'youTubeShort' ? 'reel' : ''),
          ...(oas),
        }
      };

      promise = state.editing
        ? axios.put(`/api/v1/influencers/user_post_drafts/${draft.id}`, data)
        : axios.post(`/api/v1/influencers/user_post_drafts?offer_id=${offer.id}`, data);

      promise.then(({ data }) => {
        if (data.success) {
          setState({
            ...state,
            success: true,
            action: state.editing ? ACTIONS.UPDATE : ACTIONS.CREATE,
            file: null,
            locationTags: [],
            locationTagsDownloaded: false,
            isCurrentVersion: true,
          });
          formik.resetForm();
        } else {
          throw 'Could not upload';
        }
        onRequestClose();
      }).catch(({ response }) => {
        const errorMessage = response.data && response.data.base ? response.data.base[0] : null;

        if (state.editing) {
          setState({
            ...state,
            loading: false,
          });
        } else {
          setState({
            ...state,
            success: false,
            loading: false
          });
        }

        if (errorMessage) {
          formik.setStatus({
            carousel_media_urls: errorMessage
          });

          formik.setTouched({
            carousel_media_urls: true
          });

          scrollToHashInDiv('.dropzone-wrapper', document.querySelector('.slide-pane__content'));
        } else {
          onRequestClose();
        }
      });
    },
  });

  const selectedType = useMemo(() => {
    if (state.editing) return draft.user_post_draft_type;

    return offer.user_post_draft_types.find(type => camelCase(type.label) === formik.values.contentType);
  }, [formik.values.contentType, state.editing]);

  useEffect(() => {
    const selectedUserPostDraftTypeObjAttrs = get(selectedType, 'object_attribute_metadata', []);

    userPostDraftTypeObjAttrs.forEach(attr => {
      formik.setFieldValue(attr.code, undefined, false);
    });

    object_attribute_metadata.concat(selectedUserPostDraftTypeObjAttrs).forEach(attr => {
      formik.registerField(attr.code, { validate: validateObjAttrValuesInput(attr.datatype, attr.is_multiple, attr.ui_element, (attr.optional || !attr.is_editable_by_influencer)) });
    });

    formik.setErrors({});
    formik.setTouched({});


    const fileUrls = get(draft, 'carousel_media_urls', []).map((fileUrl) => ({ fileUrl }));

    formik.setValues({
      title: get(draft, 'title', ''),
      contentType: camelCase(get(selectedType, 'label')) || formik.values.contentType,
      story: getContentType(draft) === 'story',
      caption: get(draft, 'caption', ''),
      mediaUrl: get(draft, 'media_url', ''),
      carousel_media_urls: fileUrls,
      googleWebStoryURL: get(draft, 'google_web_story_url', ''),
      locationTags: get(draft, 'location_tags') || [],
      generalTags: get(draft, 'general_tags', ''),
      ...(extractValuesFromAttrs(object_attribute_metadata, get(draft, 'object_attribute_values', {}))),
      ...(extractValuesFromAttrs(selectedUserPostDraftTypeObjAttrs, get(draft, 'object_attribute_values', {})))
    });

  }, [selectedType]);

  const renderVersioning = () => {
    const versions = (draft && draft.versions) || [];

    if (versions.length === 0) return null;

    const changeVersion = (versionSelected) => {
      const version = versionSelected.value;

      let draftVersion = versions.find(v => v.version === version);

      if (version === -1) {
        draftVersion = draft;
      }
      setState({
        ...state,
        isCurrentVersion: version === -1
      });
      setSelectedVersion(version === -1 ? null : draftVersion);
      if (!draftVersion) return;

      formik.setValues({ ...extractValues(draftVersion) });
    };

    return (
      <Form.Group>
        <Form.Label>
          {t('influencers.common.versions')}
        </Form.Label>
        <Select
          disabled={state.loading}
          defaultValue={-1}
          options={[
            {
              value: -1,
              label: t('influencers.common.current')
            },
            ...draft.versions.map(version => ({
              value: version.version,
              label: t('influencers.common.version', { version: version.version + 1 })
            }))
          ]}
          onChange={changeVersion}
        />
        {!state.isCurrentVersion &&
          <div className="draft-version-notice text-2 tc-grey1 mt-3">
            {t('influencers.pages.campaign.drafts.past_version_notice')}
          </div>
        }
      </Form.Group>
    );
  };

  const renderForm = useCallback(() => {
    let options = [];

    switch (offer.content_approval_option) {
      case 'caption_approval_only':
      case 'caption_and_content_approval':
      default:
        options.push({
          value: 'post',
          label: t('influencers.common.posts.in_feed_posts')
        });
        options.push({
          value: 'story',
          label: t('influencers.common.posts.instagram_stories', { count: 1 })
        });
        options.push({
          value: 'carousel',
          label: t('influencers.common.posts.instagram_carousel')
        });
        break;
      case 'google_web_story':
        options.push({
          value: 'googleWebStory',
          label: t('influencers.common.posts.google_web_story', { count: 1 })
        });
        break;
      case 'youtube_reels':
        options.push({
          value: 'youTubeShort',
          label: t('influencers.common.posts.youtube_shorts')
        });
        break;
    }

    if (offer.user_post_draft_types_enabled) {
      options = offer.user_post_draft_types.map(type => ({
        value: camelCase(type.label),
        label: type.label
      }));
    }

    return <Fragment>
      <div className="mt-3"></div>
      {!state.editing && <Form.Group>
        <Form.Label>
          {t('influencers.pages.campaign.drafts.content_type')}
        </Form.Label>
        <Select
          className="mb-2"
          placeholder={t('influencers.pages.campaign.drafts.content_type')}
          name='contentType'
          defaultValue={formik.values.contentType}
          error={get(formik, 'touched.contentType') && get(formik, 'errors.contentType')}
          onChange={formik.handleChange}
          disabled={state.loading}
          options={options}
        />
        {get(formik, 'touched.content_type') && get(formik, 'errors.content_type') ?
          <Form.Text className="text-muted danger-text">
            {get(formik.errors, 'content_type')}
          </Form.Text> : null}
      </Form.Group>}

      {offer.user_post_draft_types_enabled ?
        <Fragment> {renderContentTypesForm()} </Fragment> :
        <Fragment>
          {formik.values.contentType == CONTENT_TYPE.GOOGLE_WEB_STORY ?
            <Fragment>
              {renderFeedback()}
              {renderGwsContent()}
            </Fragment> :
            <Fragment>
              {renderVersioning()}
              {renderFeedback()}
              {renderYourContent()}
              {renderMediaUrl()}
              {formik.values.contentType === CONTENT_TYPE.YOUTUBE_SHORT && renderTitle()}
              {renderCaption()}
              {renderObjectAttributes()}
            </Fragment>
          }

          {formik.values.contentType === CONTENT_TYPE.YOUTUBE_SHORT &&
            <Fragment>
              {renderLocationTags()}
              {renderGeneralTags()}
            </Fragment>
          }
        </Fragment>
      }
    </Fragment>;
  }, [formik, state]);

  const renderContentTypesForm = useCallback(() => <Fragment>
    {formik.values.contentType === 'googleWebStory' ?
      <Fragment>
        {renderFeedback()}
        {draft && <Row className="mt-1 mb-1">
          <Col sm={4}><Image src={draft.gws_image} fluid style={{ width: '6em', height: '6em', objectFit: 'cover' }} /></Col>
          <Col sm={8}><p>{draft.title}</p></Col>
        </Row>}

        {renderContentTypeFields()}
      </Fragment> :
      <Fragment>
        {renderVersioning()}
        {renderFeedback()}
        {renderYourContent()}
        {renderContentTypeFields()}
        {renderObjectAttributes()}
      </Fragment>
    }
  </Fragment>, [formik, state]);

  const renderContentTypeFields = () => {
    if (!selectedType) {
      return (
        <Fragment>
          {get(formik.values, 'caption', false) && renderMediaUrl()}
          {get(formik.values, 'mediaUrl', false) && renderCaption()}
        </Fragment>
      );
    }

    const fields = Object.keys(selectedType.fields).map((field, idx) => {
      const selectedField = selectedType.fields[field];
      switch (field) {
        case 'title':
          return <Fragment key={idx}> {renderTitle(selectedField)} </Fragment>;
        case 'caption':
          return <Fragment key={idx}> {renderCaption(selectedField)} </Fragment>;
        case 'media':
        case 'carousel':
          return <Fragment key={idx}> {renderMediaUrl(selectedField)} </Fragment>;
        case 'link':
          return <Fragment key={idx}> {renderLink(selectedField)} </Fragment>;
        case 'location_tags':
          return <Fragment key={idx}> {renderLocationTags(selectedField)} </Fragment>;
        case 'general_tags':
          return <Fragment key={idx}> {renderGeneralTags(selectedField)} </Fragment>;
      }
    });

    const customFields = selectedType.object_attribute_metadata.map(ob => {
      const code = ob.code;
      const isTouched = formik.touched && formik.touched[code];
      const hasError = formik.errors && formik.errors[code];

      return (<Form.Group key={code}>
        <Col sm={12} key={code} className="p-0">
          <ObjectAttribute
            fieldData={ob}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values[code]}
            name={code}
            disabled={!ob.is_editable_by_influencer}
            error={!!(isTouched && hasError)}
            showColumnLabel
            columnLabelFirst
          />
          {isTouched && hasError ?
            <Form.Text className="text-muted danger-text">
              {hasError}
            </Form.Text> : null}
        </Col>
      </Form.Group>);
    });

    return fields.concat(customFields);
  };

  const renderTitle = (field = null) => <Form.Group>
    <Form.Label>
      {field ? field.label : t('influencers.pages.campaign.drafts.title')}
    </Form.Label>
    {['view_draft', 'view_feedback'].includes(status) ?
      <p>{formik.values.title}</p> :
      <FormControl
        placeholder={t('influencers.pages.campaign.drafts.title')}
        {...formik.getFieldProps('title')}
      />
    }
  </Form.Group>;

  const getCaptionLabel = (field = null) => {
    if (field) {
      return field.label;
    }

    return formik.values.contentType == CONTENT_TYPE.YOUTUBE_SHORT ?
      t(`influencers.common.description`) :
      t(`influencers.common.caption`);
  };

  const renderCaption = (field = null) => <Form.Group>
    <Form.Label>
      {getCaptionLabel(field)}
    </Form.Label >
    {
      ['view_draft', 'view_feedback'].includes(status) ?
        <p>{get(formik.values, 'caption', '')}</p> :
        <FormControl
          placeholder={t('influencers.common.text')}
          textarea
          name='caption'
          data-cy='upload_caption'
          value={formik.values.caption}
          disabled={!state.isCurrentVersion}
          required={formik.values.contentType === CONTENT_TYPE.POST}
          rows={5}
          onChange={formik.handleChange}
        />
    }
  </Form.Group >;

  const renderObjectAttributes = useCallback(() => {
    if (!object_attribute_metadata || !object_attribute_metadata.length) return null;

    return (<Fragment>
      <hr className='my-6' />
      <h4 className='mb-4'>
        {t('influencers.common.questions')}
      </h4>
      {
        object_attribute_metadata
          .map(ob => {
            const code = ob.code;
            const isTouched = formik.touched && formik.touched[code];
            const hasError = formik.errors && formik.errors[code];

            return (<Form.Group key={code}>
              <Row>
                <Col sm={12} key={code}>
                  <ObjectAttribute
                    fieldData={ob}
                    onChange={formik.handleChange}
                    disabled={!ob.is_editable_by_influencer}
                    onBlur={formik.handleBlur}
                    value={formik.values[code]}
                    name={code}
                    error={isTouched && hasError}
                    showColumnLabel
                    columnLabelFirst
                  />
                </Col>
              </Row>
            </Form.Group>);
          })
      }
    </Fragment>);
  }, [formik, object_attribute_metadata]);

  const dropzoneChange = useCallback((event, stateFile) => {
    if (event === DROPZONE_STATE.SUCCESS && stateFile.success) {
      if (selectedType && selectedType.fields.carousel) {
        let newFiles = [...formik.values.carousel_media_urls];

        newFiles = [
          ...newFiles,
          ...stateFile.fileUrls.map((fileUrl) => ({ fileUrl }))
        ];

        formik.setFieldValue('carousel_media_urls', newFiles);
        formik.setStatus({
          carousel_media_urls: ''
        });
      } else {
        setState({
          ...state,
          file: stateFile.file[0]
        });
        formik.setFieldValue('mediaUrl', stateFile.fileUrls[0]);
      }
    }
  }, [formik, selectedType]);

  const getMediaLabel = (field = null) => {
    if (field) {
      return field.label;
    }

    return formik.values.contentType == 'youTubeShort' ?
      t('influencers.pages.campaign.drafts.video') :
      t('influencers.pages.campaign.drafts.media');
  };

  const renderMediaUrl = (field = null) => <Form.Group>
    <Form.Label>
      {getMediaLabel(field)}
    </Form.Label>
    <div>
      {get(formik.values, 'mediaUrl', '') ?
        ((state.file && state.file.type.includes('video')) || get(draft, 'is_video'))
          ? <video controls className='w-100' key={formik.values.mediaUrl}>
            <source src={formik.values.mediaUrl}></source>
          </video>
          : <Image fluid src={formik.values.mediaUrl} key={formik.values.mediaUrl} />
        : null
      }
      <div>
        <DraggableList
          itemKey="fileUrl"
          template={CarouselListItem}
          list={get(formik.values, 'carousel_media_urls', [])}
          commonProps={{
            onItemRemove: (item) => {
              const newList = formik.values.carousel_media_urls.filter((file) => file.fileUrl != item.fileUrl);

              formik.setFieldValue('carousel_media_urls', newList);
            },
          }}
          onMoveEnd={(newList) => {
            formik.setFieldValue('carousel_media_urls', newList);
          }}
        />
      </div>

      {!['view_draft', 'view_feedback'].includes(status) &&
        state.isCurrentVersion &&
        <Dropzone
          className="mb-3 mt-2"
          name='mediaUrl'
          multiple
          selectFiles
          accept={{ 'image/*': IMAGES_TYPES, 'video/*': VIDEO_TYPES }}
          disabled={!state.isCurrentVersion}
          onChange={dropzoneChange}
        />
      }

      {!isEmpty(get(formik.status, 'carousel_media_urls')) &&
        <Form.Text className="text-muted danger-text mb-3">
          {get(formik.status, 'carousel_media_urls')}
        </Form.Text>
      }
    </div>
  </Form.Group>;

  const renderGwsContent = () => <Form.Group>
    {draft && <Row className="mt-1 mb-1">
      <Col sm={4}><Image
        src={draft.gws_image}
        fluid
        style={{ width: '6em', height: '6em', objectFit: 'cover' }} /></Col>
      <Col sm={8}><p>{draft.title}</p></Col>
    </Row>}

    {renderLink()}
  </Form.Group>;

  const renderLocationTags = (field = null) => <Form.Group>
    <Form.Label>
      {field ? field.label : t('influencers.pages.campaign.drafts.location')}
    </Form.Label>
    {['view_draft', 'view_feedback'].includes(status) ?
      <p>{get(formik.values, 'locationTag.name')}</p> :
      <Select
        isMulti
        className="mb-2"
        placeholder={t('influencers.pages.campaign.drafts.location')}
        {...formik.getFieldProps('locationTags')}
        onBlur={() => formik.setFieldTouched('locationTags')}
        options={state.locationTags.map(locationTag => ({
          label: get(locationTag, 'name'),
          value: get(locationTag, 'id')
        }))}
        value={
          get(formik.values, 'locationTags')
            .map(locationTag => ({
              label: get(locationTag, 'name'),
              value: get(locationTag, 'id')
            }))
        }
      />
    }
  </Form.Group>;

  const renderGeneralTags = (field = null) => <Form.Group>
    <Form.Label>
      {field ? field.label : t('influencers.pages.campaign.drafts.tags')}
    </Form.Label>
    {['view_draft', 'view_feedback'].includes(status) ?
      <p>{formik.values.generalTags}</p> :
      <FormControl
        placeholder={t('influencers.pages.campaign.drafts.tags')}
        textarea
        rows={5}
        {...formik.getFieldProps('generalTags')}
      />
    }
  </Form.Group>;

  const renderLink = (field = null) => <Form.Group>
    <Form.Label>
      {field ? field.label : t('influencers.pages.campaign.drafts.link_url')}
    </Form.Label>
    <FormControl
      placeholder={t('influencers.common.text')}
      name='googleWebStoryURL'
      defaultValue={formik.values.googleWebStoryURL}
      onChange={formik.handleChange}
      disabled={false}
      type="url" />
  </Form.Group>;

  // Only the feedback given on the version being viewed is shown, so drafts that haven't been reviewed skip the box
  const renderFeedback = useCallback(() => {
    const note = get(selectedVersion || draft, 'am_current_status_note');

    if (!note) return null;

    const rejected = selectedVersion
      ? !!selectedVersion.am_rejected_at
      : draft.am_review_status === CONTENT_STATUSES.REJECTED;

    return (
      <div className={`draft-feedback mb-5 ${rejected ? 'rejected' : ''}`}>
        <i className="fal fa-exclamation-circle"></i>
        <div>
          <div className="text-medium-1 mb-1">
            {t('influencers.content_approval.feedback_from_obviously')}
          </div>
          <div className="text-1" dangerouslySetInnerHTML={{ __html: note }}></div>
        </div>
      </div>
    );
  }, [draft, selectedVersion]);

  const renderYourContent = () => {
    if (!state.editing) return null;

    return (
      <div className="mb-4">
        <div className="text-medium-1 mb-1">
          {t('influencers.pages.campaign.drafts.your_content')}
        </div>
        {!['view_draft', 'view_feedback'].includes(status) &&
          <div className="text-1">
            {t('influencers.pages.campaign.drafts.your_content_description')}
          </div>
        }
      </div>
    );
  };

  const getContentTypeLabel = () => {
    if (offer.user_post_draft_types_enabled && get(draft, 'user_post_draft_type')) {
      return draft.user_post_draft_type.label;
    }

    switch (getContentType(draft)) {
      case CONTENT_TYPE.GOOGLE_WEB_STORY:
        return t('influencers.common.posts.google_web_story', { count: 1 });
      case CONTENT_TYPE.STORY:
        return t('influencers.common.posts.instagram_stories', { count: 1 });
      case CONTENT_TYPE.YOUTUBE_SHORT:
        return t('influencers.common.posts.youtube_shorts');
      default:
        return t('influencers.common.posts.in_feed_posts');
    }
  };

  const onDeleteDraft = useCallback(() => {
    axios.delete(`/api/v1/influencers/user_post_drafts/${draft.id}`).then(() => {
      setState({ ...state, success: true, action: ACTIONS.DELETE });
      onRequestClose();
    });
  }, []);

  const renderHeader = useCallback(() => !state.editing
    ? <ResponsiveHeader xs='h4' sm='h3' text={t('influencers.pages.campaign.drafts.new_draft')} />
    : <Fragment>
      <ResponsiveHeader xs='h4' sm='h3' text={t('influencers.pages.campaign.drafts.edit_your_draft')} />
      <div className="text-2 tc-grey1 mt-1">
        {getContentTypeLabel()}
      </div>
    </Fragment>, [state.editing]);

  const renderDeletePrompt = useCallback(() => <div>
    <ResponsiveHeader xs='h4' lg='h3' text={t('influencers.common.delete')} />
    <div className="body-regular-1 mt-4 mb-6">
      {t('influencers.pages.campaign.drafts.delete_prompt_message')}
    </div>
    <Row className='justify-content-end'>
      <Col className="col-auto">
        <ButtonGroup className="non-adaptable">
          <Button
            variant="secondary"
            type="button"
            onClick={() => setState({
              ...state,
              showDeletePrompt: false
            })}>
            {t('influencers.common.cancel')}
          </Button>
          <Button
            variant="primary"
            type="button"
            onClick={onDeleteDraft}
            disabled={state.loading}>
            {state.loading
              ? <Spinner animation="border" size="sm" />
              : t('influencers.common.delete')
            }
          </Button>
        </ButtonGroup>
      </Col>
    </Row>
  </div>, [draft]);

  const renderQuickCheck = () => <div>
    <div className="d-flex align-items-start justify-content-between">
      <ResponsiveHeader xs='h4' sm='h3' text={t('influencers.pages.campaign.drafts.quick_check.title')} />
      <button type="button" className="quick-check-close" onClick={() => onRequestClose()}>
        <i className="fal fa-times" />
      </button>
    </div>
    <div className="quick-check-new-content mt-5">
      <div className="text-medium-1 mb-2">
        {t('influencers.pages.campaign.drafts.quick_check.new_content_title')}
      </div>
      <div className="text-2">
        {t('influencers.pages.campaign.drafts.quick_check.new_content_body')}
      </div>
    </div>
    <div className="quick-check-rework mt-4">
      <div className="text-medium-2 mb-2">
        {t('influencers.pages.campaign.drafts.quick_check.rework_title')}
      </div>
      <div className="text-2">
        <Trans
          i18nKey='influencers.pages.campaign.drafts.quick_check.rework_body'
          components={[<strong key="edit-this-draft" />]} />
      </div>
    </div>
    <div className="mt-4">
      <Checkbox
        name="new_content_confirmation"
        id="new_content_confirmation"
        data-cy="new_content_confirmation"
        value={newContentConfirmed}
        onChange={() => setNewContentConfirmed(!newContentConfirmed)}
        label={<span className="text-2">
          {t('influencers.pages.campaign.drafts.quick_check.confirm_new_content')}
        </span>}
      />
    </div>

    <div className="my-6">
      <hr />
    </div>
    <Row className='justify-content-end'>
      <Col className="col-auto">
        <ButtonGroup className="non-adaptable">
          <Button
            variant="secondary"
            type="button"
            data-cy="quick_check_back"
            onClick={() => onRequestClose()}>
            {t('influencers.pages.campaign.drafts.quick_check.back_to_drafts')}
          </Button>
          <Button
            variant="primary"
            type="button"
            data-cy="quick_check_continue"
            disabled={!newContentConfirmed}
            onClick={() => setShowQuickCheck(false)}>
            {t('influencers.pages.campaign.drafts.quick_check.continue')}
          </Button>
        </ButtonGroup>
      </Col>
    </Row>
  </div>;

  return (
    <SlidingPane
      overlayClassName="slide-over"
      isOpen={open}
      closeIcon={null}
      width="375"
      shouldCloseOnEsc
      onRequestClose={onRequestClose}
      onAfterClose={() => onAfterClose(state.success, state.action, state.error)}
      hideHeader
      from={document.body.dir === 'ltr' ? 'right' : 'left'}
    >
      {showQuickCheck ? renderQuickCheck() : state.showDeletePrompt ?
        renderDeletePrompt()
        : <div>
          {
            (state.editing || readOnly) && draft && draft.platform !== 'other' ?
              <BadgeStatus className="m-0" status={draft.am_review_status} /> : null
          }
          {
            !readOnly && renderHeader()
          }

          <Form onSubmit={formik.handleSubmit}>
            {renderForm()}

            <div className="my-6">
              <hr />
            </div>
            <Row className='justify-content-end'>
              <Col className="col-auto">
                <ButtonGroup className="non-adaptable">
                  {
                    !['view_draft', 'view_feedback'].includes(status) &&
                    state.editing &&
                    <Button
                      variant="tertiary"
                      type="button"
                      data-cy='delete_draft'
                      onClick={() => setState({ ...state, showDeletePrompt: true })}
                      disabled={state.loading}>
                      {t('influencers.common.delete')}
                    </Button>
                  }
                  <Button
                    variant="secondary"
                    type="button"
                    onClick={() => onRequestClose()}
                    disabled={state.loading}>
                    {t('influencers.common.cancel')}
                  </Button>
                  {!['view_draft', 'view_feedback'].includes(status) &&
                    <Button
                      variant="primary"
                      data-cy='save_button'
                      type="submit"
                      disabled={
                        (state.loading || !formik.isValid || !formik.dirty || !isEmpty(get(formik.status, 'carousel_media_urls')))
                      }>
                      {state.loading
                        ? <Spinner animation="border" size="sm" />
                        : t('influencers.common.save')
                      }
                    </Button>
                  }
                </ButtonGroup>
              </Col>
            </Row>
            {!readOnly && !['view_draft', 'view_feedback'].includes(status) &&
              <div className="text-3 text-right mt-3">
                <Trans
                  i18nKey='influencers.content_approval.saving_isnt_submitting'
                  components={[<strong key="submit-for-review" />]} />
              </div>
            }
          </Form>
        </div>
      }
    </SlidingPane>
  );
};

DraftSidePane.propTypes = {
  open: PropTypes.bool,
  draft: PropTypes.any,
  readOnly: PropTypes.bool,
  confirmNewContent: PropTypes.bool,
  onRequestClose: PropTypes.func.isRequired,
  onAfterClose: PropTypes.func,
};

DraftSidePane.defaultProps = {
  open: false,
  draft: null,
  readOnly: false,
  confirmNewContent: false,
  onAfterClose: () => { },
};

export default DraftSidePane;
